import {
  QuestionnaireInterface,
  QuestionnaireResponse,
  assessmentQuestionnaireApiHandler,
} from '@api/assessmentQuestionnaireApiHandler';
import { getToken } from '@api/config';
import { QuestionnaireType } from '@enums/QuestionnaireTypes';
import { getCompanyDetails } from '@helpers/auth.helper';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useAppSelector } from '@hooks/useAppSelector';
import { useUpdateUserStatus } from '@hooks/useUserStatus';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { ApiResponse } from '@interfaces/index';
import AuthenticationLayout from '@layouts/AuthenticationLayout';
import { showToast } from '@shared/atoms';
import { LoadingBackdrop } from '@shared/molecules';
import { OnboardingIntroduction, SingleQuestionnaire } from '@shared/organisms';
import { CoreLayout } from '@shared/templates';
import { useQuery } from '@tanstack/react-query';
import { PublicCompanyDetails } from 'custom';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { RootState } from 'store';
import { getUserStatusData } from 'store/Actions/UserStatusAction';
import { setChosenQuestionnaire } from 'store/Slices/QuestionnaireSliceV2';

export const OnboardingAssessmentPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { isDesktop } = useWindowDimensions();
  const company = localStorage.getItem('company');
  const parsedCompany = JSON.parse(company || '{}');
  const { chosenQuestionnaire } = useAppSelector((state) => state.questionnaireV2 || {});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showOnboardingIntroduction, setShowOnboardingIntroduction] = useState(true);
  const companyConfigData = useAppSelector((state: RootState) => state.companyConfig);
  const [companyDetails, setCompanyDetails] = useState<PublicCompanyDetails | null>(null);

  const token = getToken();
  let organizationName;
  let organizationLogo;

  useEffect(() => {
    const getCompanyData = async () => {
      const data = await getCompanyDetails();
      if (data) {
        const compDetails = {
          companyLogo: data.company_logo,
          companyName: data.company_name,
          companyUrl: data.company_url,
          hideHapstarLogo: false,
          id: data.id,
          signUpText: data.sign_up_text,
          loginText: data.login_text,
          shareThoughts: data.share_thoughts,
          platformName: null,
        };
        setCompanyDetails(compDetails);
      }
    };

    if (token && token !== null && token !== undefined) {
      getCompanyData();
    }
  }, [token]);

  const {
    mutate: mutateStatus,
    isPending: isPendingStatus,
    isSuccess: isSuccessStatus,
  } = useUpdateUserStatus(false);

  const fetchOnboardingQuestionnaires = async (): Promise<ApiResponse<QuestionnaireResponse[]>> =>
    assessmentQuestionnaireApiHandler.getAllCompanyOnboardingQuestionnaires();

  const fetchQuestionsByQuestionnaire = async (): Promise<
    ApiResponse<QuestionnaireInterface> | undefined
  > => {
    if (!chosenQuestionnaire || !chosenQuestionnaire.questionnaireId) {
      showToast('Please select a questionnaire from the list', undefined, 'error');

      return undefined;
    }

    return assessmentQuestionnaireApiHandler.getQuestionsByQuestionnaire(
      chosenQuestionnaire.questionnaireId,
    );
  };

  const {
    data: questionnairesData,
    isLoading: questionnairesLoading,
    error: questionnairesError,
  } = useQuery({
    queryKey: ['getAllCompanyOnboardingQuestionnaires', parsedCompany.id],
    queryFn: fetchOnboardingQuestionnaires,
    enabled: !!parsedCompany?.id,
  });

  const {
    data: questionsData,
    isLoading: questionsLoading,
    error: questionsError,
    isPending,
  } = useQuery({
    queryKey: ['getOnboardingQuestionsByQuestionnaire', chosenQuestionnaire?.questionnaireId],
    queryFn: fetchQuestionsByQuestionnaire,
    enabled: !!(parsedCompany?.id && chosenQuestionnaire?.questionnaireId),
  });

  const questionnaires = useMemo(
    () => (questionnairesData?.success ? questionnairesData.responseObject : []),
    [questionnairesData],
  );

  useEffect(() => {
    if (questionnaires.length > 0) {
      dispatch(setChosenQuestionnaire(questionnaires[currentIndex]));
    }
  }, [questionnaires, currentIndex, dispatch]);

  const totalQuestionnaires = questionnaires?.length;
  const isLastQuestionnaireInBoarding = currentIndex === totalQuestionnaires - 1;

  const handleNextQuestionnaire = () => {
    const today = new Date().toISOString().split('T')[0];

    if (!isLastQuestionnaireInBoarding) {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % questionnaires.length);
    }

    if (chosenQuestionnaire?.questionnaireV2?.questionnaireType === QuestionnaireType.CHECK_IN) {
      let localStorageData: LocalStorageData = {};
      const storedData = localStorage.getItem('shortStarCacheHistory');

      if (!storedData) {
        localStorageData = {
          lastPopupDate: today,
          popupClosedToday: true,
        };
        localStorage.setItem('shortStarCacheHistory', JSON.stringify(localStorageData));
      }
    }
  };

  const handleUpdateStatus = async () => {
    await dispatch(getUserStatusData());
  };

  if (companyDetails !== null && companyDetails.id !== 0) {
    organizationName = companyDetails.companyName && companyDetails.companyName;
    organizationLogo = companyDetails.companyLogo
      ? `${process.env.PUBLIC_URL}/images/logos/${companyDetails.companyLogo}`
      : undefined;
  }

  return (
    <>
      {showOnboardingIntroduction ? (
        <AuthenticationLayout
          title="Introduction"
          organizationLogo={organizationLogo}
          organizationAltText={organizationName}
          hideHapstarLogoEnabled={companyConfigData?.data?.hideHapstarLogo}
        >
          <OnboardingIntroduction
            onComplete={() => setShowOnboardingIntroduction(false)}
            companyFeatures={{
              speakUp: companyConfigData?.data?.showSpeakUp,
              experts: companyConfigData?.data?.expertsEnabled,
              instantHelp: companyConfigData?.data?.showInstantHelp,
            }}
          />
        </AuthenticationLayout>
      ) : (
        <AuthenticationLayout
          title="Introduction"
          organizationLogo={organizationLogo}
          organizationAltText={organizationName}
          hideHapstarLogoEnabled={companyConfigData?.data?.hideHapstarLogo}
        >
          <div className="boxed">
            {isPendingStatus && <LoadingBackdrop text="Saving your data..." />}
            <SingleQuestionnaire
              companyQuestionnaires={questionnaires}
              questionsData={questionsData}
              questionnairesLoading={questionnairesLoading}
              questionnairesError={questionnairesError}
              questionsLoading={questionsLoading || isPending}
              questionsError={questionsError}
              isOnboarding
              onComplete={handleNextQuestionnaire}
              isLastQuestionnaireInBoarding={isLastQuestionnaireInBoarding}
              handleUpdateStatus={handleUpdateStatus}
            />
          </div>
        </AuthenticationLayout>
      )}
    </>
  );
};

interface LocalStorageData {
  lastPopupDate?: string;
  popupClosedToday?: boolean;
}
